import { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import FilterChip from "./FilterChip";
import FilterChipGroup from "./FilterChipGroup";
import FilterSearch from "./FilterSearch";
import { matchesQuery, orderBySelection } from "./optionUtils";

export interface FilterOptionsDialogProps {
  open: boolean;
  title: string;
  /** Plural noun for the collection, e.g. "assuntos". */
  noun: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  onClose: () => void;
  searchPlaceholder: string;
}

/**
 * The top of the overflow ladder: the complete option list for one section,
 * searchable, with everything already chosen pinned to the front (§23.7).
 */
export default function FilterOptionsDialog({
  open,
  title,
  noun,
  options,
  selected,
  onToggle,
  onClose,
  searchPlaceholder,
}: FilterOptionsDialogProps) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const visible = useMemo(() => {
    const filtered = options.filter((option) => matchesQuery(option, query));
    return orderBySelection(filtered, selected);
  }, [options, query, selected]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      width="min(760px, 94vw)"
      destroyOnClose
      className="acertei-filter-modal"
      aria-label={title}
    >
      <div className="flex max-h-[80dvh] flex-col bg-surface sm:max-h-[70vh]">
        <div className="border-b border-edge px-4 py-4 sm:px-5">
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          <p className="mt-0.5 text-[13px] text-ink-muted">
            {`${options.length} ${noun} · ${selected.length} selecionadas`}
          </p>
          <FilterSearch
            className="mt-3"
            value={query}
            onChange={setQuery}
            placeholder={searchPlaceholder}
            ariaLabel={`Buscar em ${title}`}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4 sm:px-5">
          {visible.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm font-medium text-ink">Nenhum filtro encontrado</p>
              <p className="mt-1 text-[13px] text-ink-muted">Tente outro termo de busca.</p>
            </div>
          ) : (
            <FilterChipGroup ariaLabel={title}>
              {visible.map((option) => (
                <FilterChip
                  key={option}
                  label={option}
                  selected={selected.includes(option)}
                  onToggle={() => onToggle(option)}
                />
              ))}
            </FilterChipGroup>
          )}
        </div>

        <div className="border-t border-edge px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-right sm:px-5">
          <button
            type="button"
            onClick={onClose}
            className="h-10 w-full rounded-input bg-brand px-5 text-sm font-semibold text-brand-contrast transition-[filter] hover:brightness-110 sm:w-auto"
          >
            Concluir
          </button>
        </div>
      </div>
    </Modal>
  );
}
